/**
 * Central plans screen — shows current tier and available upgrades.
 *
 * Doctrine: IN_APP_COPY.md, MONETIZATION_ARCHITECTURE.md
 * - Calm tone, no hype, no urgency.
 * - "Competitive outcomes are unchanged." on every plan card.
 */

import { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Pressable, ActivityIndicator, Alert, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { hasAtLeast } from '@/lib/subscription';
import { PLANS, startPurchase } from '@/lib/purchases';
import WaitlistModal from '@/components/WaitlistModal';
import type { WaitlistType } from '@/lib/waitlist';
import { TYPO } from '@/constants/typography';
import { Check, Crown, Shield, Building2, ArrowLeft } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export default function PaywallScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const { tier } = useSubscription();
  const { session, profile, refreshProfile } = useAuth();
  const c = theme.colors;
  const insets = useSafeAreaInsets();
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [waitlistType, setWaitlistType] = useState<WaitlistType | null>(null);

  const userId = session?.user?.id ?? '';

  // Trial countdown banner logic
  const subscriptionExpiresAt = (profile as any)?.subscription_expires_at ?? null;
  const subscriptionTier = (profile as any)?.subscription_tier ?? null;
  const onboardingTier = (profile as any)?.onboarding_tier ?? 'competitor';
  const daysLeft = subscriptionExpiresAt
    ? Math.ceil((new Date(subscriptionExpiresAt).getTime() - Date.now()) / 86400000)
    : 0;
  const isOnTrial = subscriptionTier === 'pro' && daysLeft > 0;
  const isCasual = onboardingTier === 'casual';

  const tierLabel = tier === 'free' ? 'Base' : tier === 'pro' ? 'Pro' : tier === 'coach_pro' ? 'Coach' : 'Club';
  const TierIcon = tier === 'free' ? Shield : tier === 'pro' ? Crown : tier === 'coach_pro' ? Crown : Building2;

  const handlePurchase = async (planId: string) => {
    if (!userId) return;
    setLoading(planId);
    try {
      const success = await startPurchase(planId, userId, yearly ? 'yearly' : 'monthly');
      if (success) {
        // Force-refresh profile so useSubscription sees the new tier immediately.
        // Must pass force=true to bypass the 2s debounce in useProfileStore.
        await refreshProfile(true);
      }
      // success=false means user cancelled — no alert needed
    } catch (err: any) {
      if (Platform.OS === 'web') {
        window.alert(`Purchase failed: ${err?.message || 'Something went wrong. Please try again.'}`);
      } else {
        Alert.alert('Purchase Failed', err?.message || 'Something went wrong. Please try again.', [{ text: 'OK' }]);
      }
    } finally {
      setLoading(null);
    }
  };

  const goBack = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/');
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: c.background }]}>
      <WaitlistModal
        visible={waitlistType !== null}
        type={waitlistType ?? 'user'}
        onClose={() => setWaitlistType(null)}
      />
      <Pressable onPress={goBack} hitSlop={12} style={[styles.backBtn, { paddingTop: insets.top + 8 }]} accessibilityRole="button" accessibilityLabel="Go back">
        <ArrowLeft size={24} color={c.text} />
      </Pressable>
      <ScrollView contentContainerStyle={styles.content}>
        <Text style={[styles.heading, { color: c.text }]}>Compete at the next level.</Text>
        <Text style={[styles.subtitle, { color: c.textSecondary }]}>
          Everyone gets the full competitive experience for free. Pro adds deeper insight into your training.
        </Text>

        {/* Free tier summary */}
        {tier === 'free' && (
          <View style={[styles.freeSummary, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.freeSummaryTitle, { color: c.text }]}>Free — Always Included</Text>
            {[
              'Log unlimited workouts',
              'Strength score & gym ranking',
              'Duels & challenges',
              'Volume trend (30 days)',
              'Recent PRs & exercise frequency',
              'Tournaments & leaderboard',
            ].map((f) => (
              <View key={f} style={styles.featureRow}>
                <Check size={14} color={c.textTertiary} strokeWidth={3} />
                <Text style={[styles.featureText, { color: c.textSecondary }]}>{f}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Trial countdown banner */}
        {isOnTrial && daysLeft <= 7 && (
          <View style={{ backgroundColor: '#F59E0B22', borderRadius: 6, padding: 12, marginBottom: 16, borderWidth: 1, borderColor: '#F59E0B44' }}>
            <Text style={{ color: '#F59E0B', fontWeight: '700', fontSize: 14, textAlign: 'center' }}>
              {isCasual
                ? `${daysLeft} days left on your Pro trial — enjoy it!`
                : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left. Lock it in or lose access.`}
            </Text>
          </View>
        )}

        {/* Current tier badge */}
        <View style={[styles.currentTier, { backgroundColor: c.surface, borderColor: c.accent }]}>
          <TierIcon size={20} color={c.accent} />
          <View style={styles.currentTierInfo}>
            <Text style={[styles.currentLabel, { color: c.textSecondary }]}>Current tier</Text>
            <Text style={[styles.currentValue, { color: c.text }]}>{tierLabel}</Text>
          </View>
        </View>

        {/* Monthly / Yearly toggle */}
        <View style={[styles.toggleRow, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Pressable
            style={[styles.toggleBtn, !yearly && { backgroundColor: c.accent }]}
            onPress={() => setYearly(false)}
            accessibilityRole="button"
            accessibilityLabel="Monthly billing"
            accessibilityState={{ selected: !yearly }}>
            <Text style={[styles.toggleText, { color: !yearly ? c.accentForeground : c.textSecondary }]}>
              Monthly
            </Text>
          </Pressable>
          <Pressable
            style={[styles.toggleBtn, yearly && { backgroundColor: c.accent }]}
            onPress={() => setYearly(true)}
            accessibilityRole="button"
            accessibilityLabel="Yearly billing, save 34%"
            accessibilityState={{ selected: yearly }}>
            <Text style={[styles.toggleText, { color: yearly ? c.accentForeground : c.textSecondary }]}>
              Yearly{' '}
              <Text style={{ fontSize: 11 }}>Save 34%</Text>
            </Text>
          </Pressable>
        </View>

        {/* Annual value callout */}
        {!yearly && (
          <Pressable
            style={[styles.annualCallout, { backgroundColor: c.accent + '12', borderColor: c.accent + '40' }]}
            onPress={() => setYearly(true)}
          >
            <Text style={[styles.annualCalloutText, { color: c.accent }]}>
              💡 Save 34% with annual billing — $79/year ($6.58/mo avg)
            </Text>
          </Pressable>
        )}

        {/* Plan cards — Pro only. Coach/Club shown as waitlist section below */}
        {PLANS.filter((p) => p.tier === 'pro').map((plan) => {
          const isActive = hasAtLeast(tier, plan.tier);
          const isPro = plan.tier === 'pro';
          const isCoach = plan.tier === 'coach_pro';
          const isClub = false;
          // Club tiers excluded — handled by guided flow below
          const effectiveYearly = yearly && plan.priceYearly > 0;
          const price = effectiveYearly ? plan.priceYearly : plan.priceMonthly;
          const period = effectiveYearly ? '/year' : '/mo';
          const isLoading = loading === plan.id;

          return (
            <View
              key={plan.id}
              style={[
                styles.planCard,
                { backgroundColor: c.surface, borderColor: isPro ? c.accent : c.border },
                isPro && styles.popularCard,
              ]}>
              {isPro && (
                <View style={[styles.popularBadge, { backgroundColor: c.accent }]}>
                  <Text style={[styles.popularBadgeText, { color: c.accentForeground }]}>Most Popular</Text>
                </View>
              )}

              <Text style={[styles.planName, { color: c.text }]}>{plan.name}</Text>
              <Text style={[styles.planPrice, { color: c.text }]}>
                ${price}
                <Text style={[styles.planPeriod, { color: c.textSecondary }]}>{period}</Text>
              </Text>

              {!plan.comingSoon && (
                <Text style={[styles.trialText, { color: c.accent }]}>
                  7-day free trial • Cancel anytime
                </Text>
              )}

              <View style={styles.features}>
                {plan.features.map((f) => (
                  <View key={f} style={styles.featureRow}>
                    <Check size={14} color={c.accent} strokeWidth={3} />
                    <Text style={[styles.featureText, { color: c.textSecondary }]}>{f}</Text>
                  </View>
                ))}
              </View>

              <Text style={[styles.neutral, { color: c.textTertiary }]}>
                Competitive outcomes are unchanged.
              </Text>

              {isActive ? (
                <View style={[styles.activeBadge, { backgroundColor: c.surfaceVariant }]}>
                  <Text style={[styles.activeBadgeText, { color: c.textSecondary }]}>Active</Text>
                </View>
              ) : plan.comingSoon ? (
                <>
                  <View style={[styles.activeBadge, { backgroundColor: c.surfaceVariant }]}>
                    <Text style={[styles.activeBadgeText, { color: c.textSecondary }]}>Coming Soon</Text>
                  </View>
                  <Pressable
                    onPress={() => {
                      import('expo-linking').then((Linking) => {
                        const subject = isCoach ? 'Coach%20Waitlist' : 'Waitlist';
                        Linking.openURL(`mailto:nathanael@douevenlift.com?subject=${subject}`);
                      });
                    }}>
                    <Text style={[styles.waitlistText, { color: c.accent }]}>Join the waitlist</Text>
                  </Pressable>
                </>
              ) : (
                <Pressable
                  style={({ pressed }) => [
                    styles.ctaButton,
                    { backgroundColor: c.accent, opacity: pressed ? 0.85 : 1 },
                  ]}
                  onPress={() => handlePurchase(plan.id)}
                  disabled={isLoading}>
                  {isLoading ? (
                    <ActivityIndicator color={c.accentForeground} />
                  ) : (
                    <Text style={[styles.ctaText, { color: c.accentForeground }]}>
                      Start your free trial
                    </Text>
                  )}
                </Pressable>
              )}
            </View>
          );
        })}

        {/* Coach — waitlist */}
        {[
          { type: 'coach' as const, icon: Crown, title: 'DUEL Coach', desc: 'For personal trainers. Manage clients, track their lifts, and deliver programming through DUEL.' },
          { type: 'club' as const, icon: Building2, title: 'DUEL Club', desc: 'For gyms, schools, and sports organizations. Leaderboards, challenges, and team management at scale.' },
        ].map(({ type, icon: Icon, title, desc }) => (
          <View key={type} style={[styles.planCard, { backgroundColor: c.surface, borderColor: c.border, marginTop: 8 }]}>
            <Text style={{ fontSize: 10, fontWeight: '800', letterSpacing: 1.5, color: c.textTertiary, textTransform: 'uppercase', marginBottom: 8 }}>Coming Soon</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <Icon size={18} color={c.textSecondary} />
              <Text style={[styles.planName, { color: c.textSecondary }]}>{title}</Text>
            </View>
            <Text style={{ color: c.textSecondary, fontSize: 13, lineHeight: 19, marginBottom: 16 }}>{desc}</Text>
            <Pressable
              style={[styles.upgradeBtn, { backgroundColor: c.surfaceVariant, borderWidth: 1, borderColor: c.accent }]}
              onPress={() => setWaitlistType(type)}
            >
              <Text style={[styles.upgradeBtnText, { color: c.accent }]}>{type === 'coach' ? 'Join Coach Waitlist →' : 'Join Club Waitlist →'}</Text>
            </Pressable>
          </View>
        ))}

        <Pressable
          style={styles.closeRow}
          onPress={async () => {
            setLoading('restore');
            try {
              const { restoreRevenueCatPurchases } = await import('@/lib/revenueCat');
              const result = await restoreRevenueCatPurchases();
              if (result.restored && result.tier !== 'free') {
                // Force-refresh profile so the gate clears immediately
                await refreshProfile(true);
                Alert.alert('Restored', `Your ${result.tier} subscription has been restored.`);
              } else {
                Alert.alert('No Purchases Found', 'No active subscription was found for this account.');
              }
            } catch {
              Alert.alert('Error', 'Failed to restore purchases. Please try again.');
            } finally {
              setLoading(null);
            }
          }}
          accessibilityRole="button"
          accessibilityLabel="Restore purchases"
        >
          <Text style={[styles.closeText, { color: c.accent }]}>
            {loading === 'restore' ? 'Restoring...' : 'Restore Purchases'}
          </Text>
        </Pressable>

        <Pressable style={styles.closeRow} onPress={goBack} accessibilityRole="button" accessibilityLabel="Continue without upgrading">
          <Text style={[styles.closeText, { color: c.textSecondary }]}>Continue without upgrading</Text>
        </Pressable>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  backBtn: { paddingHorizontal: 24, paddingBottom: 4 },
  content: { padding: 24, paddingBottom: 40, maxWidth: 520, alignSelf: 'center', width: '100%' },
  heading: { fontSize: 26, fontFamily: TYPO.heading.bold, marginBottom: 6 },
  subtitle: { fontSize: 14, fontFamily: TYPO.body.regular, lineHeight: 20, marginBottom: 20 },
  currentTier: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  currentTierInfo: {},
  currentLabel: { fontSize: 11, fontFamily: TYPO.body.regular, textTransform: 'uppercase', letterSpacing: 1 },
  currentValue: { fontSize: 20, fontFamily: TYPO.heading.bold },
  toggleRow: {
    flexDirection: 'row',
    borderWidth: 1,
    borderRadius: 2,
    padding: 4,
    marginBottom: 20,
  },
  toggleBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 0,
    alignItems: 'center',
  },
  toggleText: { fontSize: 14, fontFamily: TYPO.body.semibold },
  planCard: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 20,
    marginBottom: 16,
    gap: 8,
  },
  popularCard: {
    borderWidth: 2,
  },
  popularBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 2,
    marginBottom: 4,
  },
  popularBadgeText: { fontSize: 11, fontFamily: TYPO.body.bold, textTransform: 'uppercase', letterSpacing: 0.5 },
  planName: { fontSize: 22, fontFamily: TYPO.heading.bold },
  planPrice: { fontSize: 32, fontFamily: TYPO.heading.bold },
  planPeriod: { fontSize: 14, fontWeight: '400' },
  trialText: { fontSize: 13, fontFamily: TYPO.body.semibold, marginTop: -2 },
  clubSubtitle: { fontSize: 14, fontFamily: TYPO.body.regular, marginTop: -4 },
  clubPricing: { fontSize: 13, fontFamily: TYPO.body.semibold, marginTop: 4 },
  features: { gap: 8, marginTop: 8 },
  featureRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  featureText: { fontSize: 14, fontFamily: TYPO.body.regular, lineHeight: 20, flex: 1 },
  neutral: { fontSize: 11, fontStyle: 'italic', marginTop: 4 },
  ctaButton: {
    paddingVertical: 16,
    borderRadius: 0,
    alignItems: 'center',
    marginTop: 8,
  },
  ctaText: { fontSize: 16, fontFamily: TYPO.body.bold },
  upgradeBtn: {
    paddingVertical: 14,
    borderRadius: 4,
    alignItems: 'center' as const,
    marginTop: 4,
  },
  upgradeBtnText: { fontSize: 15, fontFamily: TYPO.body.bold },
  activeBadge: {
    paddingVertical: 12,
    borderRadius: 0,
    alignItems: 'center',
    marginTop: 8,
  },
  activeBadgeText: { fontSize: 14, fontFamily: TYPO.body.semibold },
  waitlistText: { fontSize: 14, fontFamily: TYPO.body.semibold, textAlign: 'center', marginTop: 8 },
  annualCallout: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 12,
    marginBottom: 16,
    alignItems: 'center',
  },
  annualCalloutText: {
    fontSize: 13,
    fontFamily: TYPO.body.semibold,
    textAlign: 'center',
  },
  freeSummary: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 16,
    gap: 6,
  },
  freeSummaryTitle: {
    fontSize: 16,
    fontFamily: TYPO.heading.bold,
    marginBottom: 4,
  },
  closeRow: { alignItems: 'center', marginTop: 8, paddingVertical: 12 },
  closeText: { fontSize: 14, fontFamily: TYPO.body.regular },
});
